/**
 * AI CONTEXT
 * Purpose: App shell; wires providers, global UI, and all routes.
 * Routes to: login, front-desk, teacher, CRUD tables, evaluation, profile, debug pages.
 * Key deps: auth/user/firebase/theme/jotai/admin contexts, Header, global modals.
 * Referenced by: Main.tsx entry render.
 * Refactor: move routes to typed config + remove ts-expect-error callback paths.
 */
import { ErrorBoundary } from "@sentry/react"
import { App as AntdAppContext } from "antd"
import { Provider as JotaiProvider } from "jotai"
import { type ReactNode, useContext, useEffect, useState } from "react"
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom"
import styled from "styled-components"

import EmptyPage from "#blocks/EmptyPage"
import ErrorFallbackPage from "#blocks/ErrorFallbackPage"
import Page from "#blocks/Page"
import PortfolioPage from "#components/PortfolioPage/PortfolioPage"
import { PORTFOLIO_PAGE_ROUTE } from "#components/PortfolioPage/portfolioPageRoute"
import { ROUTES } from "#constants/routes"
import AdminViewContextProvider from "#contexts/AdminViewContextProvider"
import AntdRethemeContextProvider from "#contexts/AntdRethemeContextProvider"
import { ApiContextProvider } from "#contexts/ApiContextProvider"
import AuthorizationContextProvider, {
  useAuthorizationContext,
} from "#contexts/AuthorizationContextProvider"
import FirebaseContextProvider from "#contexts/FirebaseContextProvider"
import { GlobalSearchContext } from "#contexts/GlobalSearchContextProvider"
import KeyListenerContextProvider from "#contexts/KeyListenerContextProvider"
import ThemeContextProvider from "#contexts/ThemeContextProvider"
import UserContextProvider, {
  useUserContext,
} from "#contexts/UserContextProvider"
import { useCanAccessRoute } from "#hooks/newHooks/useHasPermission"
import { useSemesterCalendar } from "#hooks/newHooks/useSemesterCalendar"
import useAnalytics from "#hooks/useAnalytics"
import ArtworkWallDebugDashboard from "#views/ArtworkWallDebugDashboard"
import ClassesTable from "#views/ClassesTable"
import CurriculumsTable from "#views/CurriculumsTable"
import EvalDebugDashboard from "#views/EvalDebugDashboard"
import BatchPrintDrawer from "#views/EvaluationView/BatchPrintDrawer"
import EvaluationView from "#views/EvaluationView/EvaluationView"
import Login from "#views/Login"
import MakeupsTable from "#views/MakeupsTable"
import NotificationDebugDashboard from "#views/NotificationDebugDashboard"
import PortfolioDebugDashboard from "#views/PortfolioDebugDashboard"
import ProjectsTable from "#views/ProjectsTable"
import QualityCheckView from "#views/QualityCheck/QualityCheckView"
import SemestersTable from "#views/SemestersTable"
import Settings from "#views/Settings"
import StandoutReviewView from "#views/StandoutReview/StandoutReviewView"
import StudentsTable from "#views/StudentsTable"
import SuperView from "#views/SuperView/SuperView"
import TagsTable from "#views/TagsTable"
import TeacherView from "#views/TeacherView/TeacherView"
import Test from "#views/Test"
import TryoutsTable from "#views/TryoutsTable"
import UsersTable from "#views/UsersTable"

import CommandPalette from "./CommandPalette"
import { RBACExample } from "./examples/RBACExample"
import GlobalPrintQueueOpener from "./GlobalPrintQueueOpener"
import Header from "./Header"
import HelpRequestButton from "./HelpRequest/HelpRequestButton"
import HelpRequestContextProvider from "./HelpRequest/HelpRequestContextProvider"
import HelpRequestModal from "./HelpRequest/HelpRequestModal"
import UserPersonalProfile from "./UserPersonalProfile"
import VersionChecker from "./VersionChecker"

const SPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  justify-content: flex-start;
  width: 100vw;
  height: 100vh;
  background-color: ${({ theme }) => theme.white};
  overflow-x: hidden;
`

const RoutePermissionGate = ({
  route,
  children,
}: {
  route: string
  children: ReactNode
}) => {
  const canAccessRoute = useCanAccessRoute(route)

  if (canAccessRoute) {
    return children
  }

  return (
    <Page>
      <EmptyPage />
    </Page>
  )
}

const BaseApp = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const { isManagerLoggedIn } = useAuthorizationContext()
  const { isPinLoggedIn } = useUserContext()
  const { trackPageView } = useAnalytics()
  useSemesterCalendar()

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      // console.log("e.code => ", e.code)
      // console.log("e.ctrlKey => ", e.ctrlKey)
      const meta = e.getModifierState("Meta")
      if (e.code === "Slash" && meta) setIsSearchOpen(true)
    }
    document.addEventListener("keydown", listener)

    return () => {
      document.removeEventListener("keydown", listener)
    }
  }, [])

  const { callbacks } = useContext(GlobalSearchContext)
  const navigate = useNavigate()

  const getCallback = (val: string) =>
    // @ts-expect-error bad types
    callbacks.find(({ label }) => label === val)

  const location = useLocation()

  // Track page views
  useEffect(() => {
    if (isManagerLoggedIn && isPinLoggedIn) {
      trackPageView()
    }
  }, [location.pathname, isManagerLoggedIn, isPinLoggedIn, trackPageView])

  // Redirect to login if not logged in and not on login page
  useEffect(() => {
    if (
      (!isManagerLoggedIn || !isPinLoggedIn) &&
      !location.pathname.includes("/login")
    ) {
      console.log("Not logged in, redirecting to /login")
      navigate("/login")
    }
  }, [isManagerLoggedIn, isPinLoggedIn, navigate, location.pathname])

  return (
    <SPageContainer data-testid="app-shell">
      <VersionChecker />
      <Header />
      {/* Allow users to multitask while printing */}
      {isManagerLoggedIn && isPinLoggedIn ? (
        <>
          <BatchPrintDrawer />
          <GlobalPrintQueueOpener />
        </>
      ) : null}
      <HelpRequestContextProvider>
        <HelpRequestButton />
        <HelpRequestModal />
      </HelpRequestContextProvider>
      {isSearchOpen && (
        // @ts-expect-error bad types
        <CommandPalette
          // options={}
          getCallback={getCallback}
          // callbacks={callbacks}
          closeSearch={() => {
            setIsSearchOpen(false)
          }}
        />
      )}
      <ErrorBoundary
        fallback={({ error }) => <ErrorFallbackPage error={error as Error} />}
      >
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* <Route path="/logout" element={<Logout />} /> */}
          {/* <Route path={ROUTES.DATABASE} element={<DatabaseTable />} /> */}
          <Route
            path={ROUTES.PROJECTS}
            element={
              <RoutePermissionGate route={ROUTES.PROJECTS}>
                <ProjectsTable />
              </RoutePermissionGate>
            }
          />
          <Route
            path={ROUTES.CURRICULUM_PLANS}
            element={
              <RoutePermissionGate route={ROUTES.CURRICULUM_PLANS}>
                <CurriculumsTable />
              </RoutePermissionGate>
            }
          />
          <Route
            path={ROUTES.EVALUATIONS}
            element={
              <RoutePermissionGate route={ROUTES.EVALUATIONS}>
                <EvaluationView />
              </RoutePermissionGate>
            }
          />
          <Route
            path={ROUTES.USERS}
            element={
              <RoutePermissionGate route={ROUTES.USERS}>
                <UsersTable />
              </RoutePermissionGate>
            }
          />
          <Route
            path={ROUTES.SETTINGS}
            element={
              <RoutePermissionGate route={ROUTES.SETTINGS}>
                <Settings />
              </RoutePermissionGate>
            }
          />

          {/* <Route path={ROUTES.SUPER_VIEW} element={<SuperView />} /> */}
          <Route
            path={ROUTES.FRONT_DESK_VIEW}
            element={
              <RoutePermissionGate route={ROUTES.FRONT_DESK_VIEW}>
                <SuperView />
              </RoutePermissionGate>
            }
          />
          <Route
            path={ROUTES.TEACHER_VIEW}
            element={
              <RoutePermissionGate route={ROUTES.TEACHER_VIEW}>
                <TeacherView />
              </RoutePermissionGate>
            }
          />

          <Route
            path={ROUTES.STUDENTS}
            element={
              <RoutePermissionGate route={ROUTES.STUDENTS}>
                <StudentsTable />
              </RoutePermissionGate>
            }
          />
          <Route
            path={PORTFOLIO_PAGE_ROUTE}
            element={
              <RoutePermissionGate route={PORTFOLIO_PAGE_ROUTE}>
                <PortfolioPage />
              </RoutePermissionGate>
            }
          />
          <Route
            path={ROUTES.CLASSES}
            element={
              <RoutePermissionGate route={ROUTES.CLASSES}>
                <ClassesTable />
              </RoutePermissionGate>
            }
          />
          <Route
            path={ROUTES.MAKEUPS}
            element={
              <RoutePermissionGate route={ROUTES.MAKEUPS}>
                <MakeupsTable />
              </RoutePermissionGate>
            }
          />
          <Route
            path={ROUTES.TRYOUTS}
            element={
              <RoutePermissionGate route={ROUTES.TRYOUTS}>
                <TryoutsTable />
              </RoutePermissionGate>
            }
          />
          <Route
            path={ROUTES.QUALITY_CHECK}
            element={
              <RoutePermissionGate route={ROUTES.QUALITY_CHECK}>
                <QualityCheckView />
              </RoutePermissionGate>
            }
          />
          <Route
            path={ROUTES.STANDOUT_REVIEW}
            element={
              <RoutePermissionGate route={ROUTES.STANDOUT_REVIEW}>
                <StandoutReviewView />
              </RoutePermissionGate>
            }
          />

          <Route
            path={ROUTES.TAGS}
            element={
              <RoutePermissionGate route={ROUTES.TAGS}>
                <TagsTable />
              </RoutePermissionGate>
            }
          />
          <Route
            path={ROUTES.SEMESTERS}
            element={
              <RoutePermissionGate route={ROUTES.SEMESTERS}>
                <SemestersTable />
              </RoutePermissionGate>
            }
          />
          <Route
            path={ROUTES.TEST}
            element={
              <RoutePermissionGate route={ROUTES.TEST}>
                <Test />
              </RoutePermissionGate>
            }
          />
          <Route
            path="/eval-debug"
            element={
              <RoutePermissionGate route="/eval-debug">
                <EvalDebugDashboard />
              </RoutePermissionGate>
            }
          />
          <Route
            path="/notification-debug"
            element={
              <RoutePermissionGate route="/notification-debug">
                <NotificationDebugDashboard />
              </RoutePermissionGate>
            }
          />
          <Route
            path="/portfolio-debug"
            element={
              <RoutePermissionGate route="/portfolio-debug">
                <PortfolioDebugDashboard />
              </RoutePermissionGate>
            }
          />
          <Route
            path="/artwork-wall-debug"
            element={
              <RoutePermissionGate route="/artwork-wall-debug">
                <ArtworkWallDebugDashboard />
              </RoutePermissionGate>
            }
          />
          <Route
            path={ROUTES.PROFILE}
            element={
              <RoutePermissionGate route={ROUTES.PROFILE}>
                <UserPersonalProfile />
              </RoutePermissionGate>
            }
          />
          <Route
            path="/auth-test"
            element={
              <RoutePermissionGate route={ROUTES.AUTH_TEST}>
                <RBACExample />
              </RoutePermissionGate>
            }
          />

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </ErrorBoundary>
    </SPageContainer>
  )
}

// Add all contexts here
const App = () => {
  return (
    <BrowserRouter>
      <AuthorizationContextProvider>
        <JotaiProvider>
          <ApiContextProvider>
            <FirebaseContextProvider>
              <UserContextProvider>
                <ThemeContextProvider>
                  <AntdRethemeContextProvider>
                    <AntdAppContext>
                      <KeyListenerContextProvider>
                        <AdminViewContextProvider>
                          <BaseApp />
                        </AdminViewContextProvider>
                      </KeyListenerContextProvider>
                    </AntdAppContext>
                  </AntdRethemeContextProvider>
                </ThemeContextProvider>
              </UserContextProvider>
            </FirebaseContextProvider>
          </ApiContextProvider>
        </JotaiProvider>
      </AuthorizationContextProvider>
    </BrowserRouter>
  )
}

export default App
App.displayName = "App"
